import { ElementPreset, ElementPresetsV } from "@/types/presets";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { pipe } from "fp-ts/function";
import { fold } from "fp-ts/Either";
import { _Error, logErrorsToUiStore } from "@/lib/wordpress/data";
import { documentStore, globalStore } from "@/store/store-accessor";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import * as t from "io-ts";
import { isUiPanelOpen } from "@/lib/panels/panels";
import { breakdanceUndo } from "@/lib/undo/undo";
import { v4 as uuidv4 } from "uuid";
import { findNode } from "@/lib/tree/tree";
import { Control } from "@/types/controls";
import defensiveCopy from "@/util/defensive-copy";
import lodash from "lodash";
import { TreeNode } from "@/types/tree";
import { BuilderElement } from "@/types/elements";

export function addPreset(newPreset: ElementPreset) {
  breakdanceUndo.transaction(
    () => globalStore.setPresets([...globalStore.presets, newPreset]),
    "Add Preset"
  );
}

export function addPresetToStoreAndSavePresetsToWordPressIfPresetsPanelIsClosed(
  preset: ElementPreset
) {
  addPreset(preset);

  if (!isUiPanelOpen("presets")) {
    savePresetsToWordPress();
  }
}

export function createElementPresetFromName(
  name: string,
  availableForType: string,
  design: any = {}
): ElementPreset {
  return {
    id: uuidv4(),
    name,
    availableForType,
    design
  };
}

export async function refetchPresetsFromServerToGlobalStore(): Promise<boolean> {
  const presetsFromServerOrErrors = await makeWordPressAjaxRequest({
    action: "breakdance_load_presets",
    decoder: ElementPresetsV
  });

  return pipe(
    presetsFromServerOrErrors,
    fold(
      errors => {
        logErrorsToUiStore(
          errors.map(err => {
            return {
              ...err,
              customNotificationToShow: err.customNotificationToShow
                ? err.customNotificationToShow.type === "INTERNAL_SERVER_ERROR"
                  ? { ...err.customNotificationToShow, keepOpen: true }
                  : err.customNotificationToShow
                : undefined
            };
          })
        );
        return false;
      },
      loadedData => {
        breakdanceUndo.ignore(() => {
          globalStore.setPresets(loadedData);
          globalStore.markCurrentPresetsAsUntouched();
        });
        return true;
      }
    )
  );
}

export async function savePresetsToWordPress(): Promise<boolean> {
  const presets = globalStore.presets.map(preset => {
    // Prevent empty objects from being turned into arrays in PHP.
    const isDesignEmpty = Object.keys(preset.design).length === 0;
    const design = isDesignEmpty ? null : preset.design;

    return {
      ...preset,
      design
    };
  });

  return pipe(
    await makeWordPressAjaxRequest({
      friendlyName: "Saving presets to WordPress",
      action: "breakdance_save_presets",
      dataToPost: {
        presets: fpJsonStringify(presets)
      },
      decoder: t.unknown
    }),
    fold(
      (errors: _Error[]) => {
        logErrorsToUiStore(errors);
        return false;
      },
      () => {
        breakdanceUndo.ignore(() =>
          globalStore.markCurrentPresetsAsUntouched()
        );
        return true;
      }
    )
  );
}

export function addPresetToElement(
  presetId: string,
  elementId: number,
  clearDesign = true
) {
  if (clearDesign) {
    documentStore.unthrottledPropertyChanged({
      elementId: elementId,
      path: "design",
      meta: {
        snapshotLabel: `Reset design of element`
      },
      value: null
    });
  }

  documentStore.unthrottledPropertyChanged({
    elementId: elementId,
    path: "meta.preset",
    meta: {
      snapshotLabel: `Add preset to element`
    },
    value: presetId
  });
}

export function removePresetFromElement(elementId: number) {
  documentStore.unthrottledPropertyChanged({
    elementId: elementId,
    path: "meta.preset",
    meta: {
      snapshotLabel: `Remove preset of a element`
    },
    value: null
  });
}

export function copyPresetPropertiesToElement(
  presetId: string,
  elementId: number
) {
  const preset = globalStore.presets.find(p => p.id === presetId);
  if (!preset) return;

  documentStore.unthrottledPropertyChanged({
    elementId: elementId,
    path: "meta.preset",
    meta: {
      snapshotLabel: `Remove preset of a element`
    },
    value: null
  });

  documentStore.unthrottledPropertyChanged({
    elementId: elementId,
    path: "design",
    meta: {
      snapshotLabel: `Copy preset properties to element`
    },
    value: preset.design
  });
}

export function doesElementHavePreset(elementId: number | undefined): boolean {
  if (!elementId) return false;
  const foundNode = findNode(documentStore.document.tree, elementId);
  if (!foundNode) return false;
  return !!foundNode.data.properties?.meta?.preset;
}

export function removeContentSectionConditions(controls: Control[]) {
  return controls.map(control => {
    const newControl = defensiveCopy(control);

    if (Array.isArray(newControl.options.condition)) {
      newControl.options.condition = newControl.options.condition.map(
        conditions => conditions.filter(c => !c.path.startsWith("content."))
      );
    } else if (newControl.options.condition?.path.startsWith("content.")) {
      newControl.options.condition = undefined;
    }

    if (newControl.children.length) {
      newControl.children = removeContentSectionConditions(newControl.children);
    }

    return newControl;
  });
}

export function mergePresetWithElementDesign(
  presetDesign: ElementPreset["design"],
  elementDesign: any
): any {
  const designA = defensiveCopy(presetDesign);
  const designB = defensiveCopy(elementDesign);

  // Merge the preset design with the element design,
  // but don't overwrite the preset design with null or undefined values
  return lodash.mergeWith({}, designA, designB, (a, b) =>
    b === null ? a : undefined
  );
}

export function getDesignForElementFromPreset(
  element: TreeNode
): ElementPreset["design"] {
  const elementDesign = element?.data.properties?.design;

  const presetId = element?.data.properties?.meta?.preset;
  const preset = globalStore.presets.find(preset => preset.id === presetId);

  return mergePresetWithElementDesign(preset?.design, elementDesign);
}

export function getElementCssWhenPresetIsApplied(element: BuilderElement) {
  return element.cssTemplate.split(
    "{#%--- Auto Generated Twig Code: ---%#}"
  )[1];
}
